
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/use-profile';
import { AlertTriangle, CheckCircle, Eye, RefreshCw, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BulletinModerationItem {
  id: string;
  content: string;
  image_url?: string;
  moderation_status: string;
  created_at: string;
  moderated_at?: string;
  is_mature?: boolean;
  profiles: {
    username: string;
  };
}

const ModerationManagement: React.FC = () => {
  const { user } = useAuth();
  const { profile } = useProfile(user?.id);
  const { toast } = useToast();
  const [bulletins, setBulletins] = useState<BulletinModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  // Only show to admins
  if (!profile?.is_admin) {
    return null;
  }

  const fetchBulletins = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bulletins')
        .select(`
          id,
          content,
          image_url,
          moderation_status,
          created_at,
          moderated_at,
          is_mature,
          profiles(username)
        `)
        .in('moderation_status', ['pending', 'error'])
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      // Transform data to match interface (profiles is always an array from Supabase)
      const transformedData = (data || []).map(bulletin => ({
        ...bulletin,
        profiles: Array.isArray(bulletin.profiles) ? bulletin.profiles[0] : bulletin.profiles
      }));
      
      setBulletins(transformedData);
    } catch (error) {
      console.error('Error fetching bulletins for moderation:', error);
      toast({
        title: "Error",
        description: "Failed to fetch bulletins for moderation",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBulletins();
  }, []);

  const updateBulletinStatus = async (bulletinId: string, status: 'approved' | 'flagged', isMature: boolean = false) => {
    setProcessing(prev => new Set(prev).add(bulletinId));
    
    try {
      const { error } = await supabase
        .from('bulletins')
        .update({
          moderation_status: status,
          is_mature: isMature,
          moderated_at: new Date().toISOString()
        })
        .eq('id', bulletinId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Bulletin ${status} successfully`,
      });

      await fetchBulletins();
    } catch (error) {
      console.error('Error updating bulletin status:', error);
      toast({
        title: "Error",
        description: `Failed to ${status} bulletin`,
        variant: "destructive"
      });
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(bulletinId);
        return newSet;
      });
    }
  };

  const resetAllErrorPosts = async () => {
    try {
      // Reset all error posts to approved
      const { error } = await supabase
        .from('bulletins')
        .update({
          moderation_status: 'approved',
          is_mature: false,
          moderated_at: new Date().toISOString()
        })
        .eq('moderation_status', 'error');

      if (error) throw error;

      // Also reset the specific failed post
      const { error: specificError } = await supabase
        .from('bulletins')
        .update({
          moderation_status: 'approved',
          is_mature: false,
          moderated_at: new Date().toISOString()
        })
        .eq('id', '64d293aa-5e60-427f-8daa-cf8315b1b524');

      if (specificError) {
        console.error('Error updating specific post:', specificError);
      }

      toast({
        title: "Success",
        description: "All error posts have been approved and should now appear in the feed",
      });

      await fetchBulletins();
    } catch (error) {
      console.error('Error resetting error posts:', error);
      toast({
        title: "Error",
        description: "Failed to reset error posts",
        variant: "destructive"
      });
    }
  };

  const triggerModeration = async () => {
    try {
      const { error } = await supabase.functions.invoke('process-pending-moderation');
      
      if (error) throw error;

      toast({
        title: "Success",
        description: "Moderation process triggered",
      });

      setTimeout(() => {
        fetchBulletins();
      }, 2000);
    } catch (error) {
      console.error('Error triggering moderation:', error);
      toast({
        title: "Error",
        description: "Failed to trigger moderation process",
        variant: "destructive"
      });
    }
  };

  const getModerationStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-500/10 text-green-500';
      case 'flagged': return 'bg-red-500/10 text-red-500';
      case 'pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'error': return 'bg-orange-500/10 text-orange-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Moderation Management</span>
          <div className="flex gap-2">
            <Button onClick={triggerModeration} size="sm" variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Trigger Moderation
            </Button>
            <Button onClick={resetAllErrorPosts} size="sm" variant="outline">
              Approve All Error Posts
            </Button>
            <Button onClick={fetchBulletins} size="sm" variant="outline">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4">Loading bulletins...</div>
        ) : bulletins.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No bulletins requiring moderation</div>
        ) : (
          <div className="space-y-4">
            {bulletins.map((bulletin) => (
              <div key={bulletin.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getModerationStatusColor(bulletin.moderation_status)}>
                        {bulletin.moderation_status}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        by @{bulletin.profiles.username}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(bulletin.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm mb-2">{bulletin.content}</p>
                    <div className="text-xs font-mono text-gray-500">
                      ID: {bulletin.id}
                    </div>
                  </div>
                  
                  {bulletin.image_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(bulletin.image_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View Image
                    </Button>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => updateBulletinStatus(bulletin.id, 'approved')}
                    disabled={processing.has(bulletin.id)}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={() => updateBulletinStatus(bulletin.id, 'flagged', false)}
                    disabled={processing.has(bulletin.id)}
                    variant="destructive"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Flag (Safe)
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={() => updateBulletinStatus(bulletin.id, 'flagged', true)}
                    disabled={processing.has(bulletin.id)}
                    variant="destructive"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Flag (NSFW)
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ModerationManagement;
